import React, { useState, useRef, memo } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
    COLUMNS,
    SUPPLIER_COLUMN,
    BRANCH_COLUMN,
    num,
    money,
    coverValue,
    branchIdOf,
    tableMinWidth
} from '../reorderUtils';

/**
 * The reorder item table.
 *
 * Two performance measures, both taken on the real 6,296-item sheet:
 *
 *  - Rendering every row cost 126,445 DOM nodes and ~146ms per scroll step,
 *    about 7fps. With `virtualize` only the rows in the viewport are mounted,
 *    so the cost is constant however large the sheet grows. Spacer rows above
 *    and below hold the scrollbar at the right length, which keeps this a real
 *    <table> — `table-layout: fixed`, the <colgroup> widths and the sticky
 *    header all still work, which they would not with positioned divs.
 *
 *  - Cells are plain text until clicked. Mounting a <select> and an <input> on
 *    every row cost roughly 3x a read-only table, making form controls a bigger
 *    expense than the row count. At most one cell is ever live.
 *
 * The trade-off of virtualising: the browser's own find-in-page only sees
 * mounted rows, so the search box above the table is how you find a part.
 */

/** Estimate only — descriptions wrap, so rows are measured once mounted. */
const ROW_HEIGHT = 32;
const OVERSCAN = 12;

/** Memoised so scrolling only re-renders rows entering the window. */
const ItemRow = memo(function ItemRow({
    item,
    columns,
    monthCover,
    editable,
    busyPart,
    onEdit,
    onReset,
    fallbackBranchId,
    activeCell,
    setActiveCell,
    index,
    measureRef
}) {
    const canEdit = editable && Boolean(branchIdOf(item, fallbackBranchId));

    const overrideMark = item.manual_override ? (
        <span className="sr-edited" title="Manually edited">
            ✎
        </span>
    ) : null;

    const needsReorderCell = () => {
        const id = `${item.part_number}:r`;
        if (canEdit && activeCell === id) {
            return (
                <select
                    className="sr-cell-input"
                    autoFocus
                    value={item.needs_reorder ? 'yes' : 'no'}
                    disabled={busyPart === item.part_number}
                    onBlur={() => setActiveCell('')}
                    onChange={(e) => {
                        onEdit(item, { needsReorder: e.target.value === 'yes' });
                        setActiveCell('');
                    }}
                >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                </select>
            );
        }
        const badge = `sr-badge-yn ${item.needs_reorder ? 'yes' : 'no'}`;
        if (!canEdit) {
            return (
                <>
                    <span className={badge}>{item.needs_reorder ? 'Yes' : 'No'}</span>
                    {overrideMark}
                </>
            );
        }
        // A real button, and shaped like a select: the caret is what tells you
        // at a glance that this cell is a control and not just a label.
        return (
            <>
                <button
                    type="button"
                    className={`${badge} editable`}
                    title="Click to change"
                    onClick={() => setActiveCell(id)}
                >
                    {item.needs_reorder ? 'Yes' : 'No'}
                    <span className="sr-caret" aria-hidden="true">
                        ▾
                    </span>
                </button>
                {overrideMark}
            </>
        );
    };

    const qtyCell = () => {
        const id = `${item.part_number}:q`;
        if (canEdit && activeCell === id) {
            return (
                <input
                    className="sr-cell-input sr-cell-num"
                    type="number"
                    min="0"
                    step="1"
                    autoFocus
                    defaultValue={item.reorder_qty}
                    disabled={busyPart === item.part_number}
                    onBlur={(e) => {
                        const qty = Math.max(0, Math.round(Number(e.target.value) || 0));
                        if (qty !== item.reorder_qty) onEdit(item, { reorderQty: qty });
                        setActiveCell('');
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') e.target.blur();
                        if (e.key === 'Escape') setActiveCell('');
                    }}
                />
            );
        }
        if (!canEdit) return num(item.reorder_qty);
        // Drawn as the text field it becomes when clicked, so the cell reads as
        // something you type into rather than a number that happens to respond.
        return (
            <span className="sr-qty-cell">
                <button
                    type="button"
                    className="sr-editable-num"
                    title="Click to change"
                    onClick={() => setActiveCell(id)}
                >
                    {num(item.reorder_qty)}
                </button>
                {item.manual_override && (
                    <button
                        type="button"
                        className="sr-btn sr-btn-sm"
                        title="Reset to the calculated values"
                        disabled={busyPart === item.part_number}
                        onClick={() => onReset(item)}
                    >
                        ↺
                    </button>
                )}
            </span>
        );
    };

    const cell = (col) => {
        switch (col.key) {
            case 'branch':
                return item.branch;
            case 'part_number':
                return (
                    <>
                        {item.part_number}
                        {item.multiple_suppliers && (
                            <span
                                className="sr-tag sr-tag-warn"
                                title={`Mapped to more than one supplier: ${(
                                    item.supplier_candidates || []
                                )
                                    .map((c) => `${c.name} (${c.count})`)
                                    .join(
                                        ', '
                                    )}. "${item.supplier}" was picked as the most frequent — fix it under Supplier Mapping if that is wrong.`}
                            >
                                ⚠
                            </span>
                        )}
                    </>
                );
            case 'description':
                return (
                    <span className="sr-desc" title={item.description}>
                        {item.description}
                    </span>
                );
            case 'supplier':
                return (
                    <span className="sr-desc" title={item.supplier}>
                        {item.supplier || 'Unmapped'}
                    </span>
                );
            case 'unit':
                return item.unit || '';
            case 'days_of_cover':
                return coverValue(item, monthCover);
            case 'needs_reorder':
                return needsReorderCell();
            case 'reorder_qty':
                return qtyCell();
            case 'reorder_value':
                return money(item.reorder_value);
            default:
                return num(item[col.key]);
        }
    };

    return (
        // `data-index` is how the virtualizer maps a measured element back to
        // its row; without it measureElement silently does nothing.
        <tr
            ref={measureRef}
            data-index={index}
            className={item.needs_reorder ? 'sr-row-flag' : ''}
        >
            {columns.map((col) => (
                <td
                    key={col.key}
                    className={`${col.right ? 'sr-right' : ''}${col.wrap ? ' sr-wrap' : ''}`.trim()}
                >
                    {cell(col)}
                </td>
            ))}
        </tr>
    );
});

const ItemsTable = ({
    items,
    isCombined = false,
    monthCover = false,
    sort,
    onSort,
    editable = false,
    busyPart = '',
    onEdit,
    onReset,
    fallbackBranchId = '',
    showSupplier = false,
    virtualize = false,
    height = 600,
    emptyLabel = 'Nothing matches these filters.'
}) => {
    const [activeCell, setActiveCell] = useState('');
    const scrollRef = useRef(null);

    const columns = [
        ...(isCombined ? [BRANCH_COLUMN] : []),
        ...COLUMNS.slice(0, 2),
        ...(showSupplier ? [SUPPLIER_COLUMN] : []),
        ...COLUMNS.slice(2)
    ];

    // Hooks cannot be conditional, so the virtualizer always runs; a count of
    // zero makes it inert when this table renders every row itself.
    //
    // Rows are measured rather than assumed: a wrapped description makes a row
    // two or three lines tall, and a fixed estimate would leave the scrollbar
    // shorter than the content and clip the last rows out of reach.
    const virtualizer = useVirtualizer({
        count: virtualize ? items.length : 0,
        getScrollElement: () => scrollRef.current,
        estimateSize: () => ROW_HEIGHT,
        overscan: OVERSCAN
    });

    const label = (col) =>
        col.key === 'days_of_cover' && monthCover ? 'Month Cover' : col.label;

    const header = (col) => (
        <th
            key={col.key}
            className={`${col.right ? 'sr-right' : ''} ${onSort ? 'sr-sortable' : ''}`}
            onClick={onSort ? () => onSort(col.key) : undefined}
            title={onSort ? `Sort by ${label(col)}` : undefined}
        >
            {label(col)}
            {sort?.key === col.key ? (sort.asc ? ' ▲' : ' ▼') : ''}
        </th>
    );

    const rowProps = {
        columns,
        monthCover,
        editable,
        busyPart,
        onEdit,
        onReset,
        fallbackBranchId,
        activeCell,
        setActiveCell
    };

    const rowKey = (item) => `${branchIdOf(item, fallbackBranchId)}-${item.part_number}`;

    let body;
    if (virtualize) {
        const virtualRows = virtualizer.getVirtualItems();
        const paddingTop = virtualRows.length ? virtualRows[0].start : 0;
        const paddingBottom = virtualRows.length
            ? virtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
            : 0;
        body = (
            <>
                {paddingTop > 0 && <tr aria-hidden="true" style={{ height: paddingTop }} />}
                {virtualRows.map((vr) => (
                    <ItemRow
                        key={rowKey(items[vr.index])}
                        item={items[vr.index]}
                        index={vr.index}
                        measureRef={virtualizer.measureElement}
                        {...rowProps}
                    />
                ))}
                {paddingBottom > 0 && <tr aria-hidden="true" style={{ height: paddingBottom }} />}
            </>
        );
    } else {
        body = items.map((item, i) => (
            <ItemRow key={rowKey(item)} item={item} index={i} {...rowProps} />
        ));
    }

    return (
        <div
            className={`sr-table-wrap${virtualize ? ' sr-virtual-wrap' : ''}`}
            ref={scrollRef}
            style={virtualize ? { height } : undefined}
        >
            {/* The floor grows with the optional Supplier/Branch columns so
                they cost extra scroll rather than squeezing every other
                column — see tableMinWidth. */}
            <table className="sr-table" style={{ minWidth: tableMinWidth(columns) }}>
                {/* Fixed layout needs the widths up front — see COLUMNS. */}
                <colgroup>
                    {columns.map((col) => (
                        <col key={col.key} style={{ width: col.width }} />
                    ))}
                </colgroup>
                <thead>
                    <tr>{columns.map(header)}</tr>
                </thead>
                <tbody>
                    {body}
                    {items.length === 0 && (
                        <tr>
                            <td colSpan={columns.length} className="sr-empty">
                                {emptyLabel}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default ItemsTable;
